/**
 * CustomerCreditHistoryTab
 *
 * Sub-aba "Créditos & Ajustes" do modal de cliente. Exibe o histórico vitalício
 * de movimentos de crédito/débito gerados por ajustes pro-rata, mid-cycle,
 * atualizações de itens e ciclos — agregando todas as assinaturas do cliente.
 *
 * Fonte imutável: `financeiro_assinatura_eventos` (sobrevive à exclusão de
 * cobranças) + snapshot atual de `financeiro_creditos`.
 *
 * RELEASE: 2026-08-19 v1.3.0 - Detalha o cancelamento manual: card do evento
 *                              `credit_canceled_manual` exibe valor cancelado,
 *                              origem, valor original/usado, ID do crédito e o
 *                              motivo informado; nova seção "Créditos cancelados".
 * RELEASE: 2026-08-19 v1.2.0 - Permite cancelar manualmente um crédito ativo
 *                              (botão na tabela de saldos, gated por
 *                              financial_charges.edit) e exibe o evento
 *                              `credit_canceled_manual` na linha do tempo.
 * RELEASE: 2026-06-27 v1.1.0 - Card de evento agora exibe o crédito do cliente
 *                              efetivamente aplicado (`creditos_consumidos`) e o
 *                              valor da cobrança gerada após o abatimento, com
 *                              uma micro-linha explicativa do cálculo
 *                              (débito bruto − crédito aplicado = cobrança gerada).
 * RELEASE: 2026-06-22 v1.0.0 - Versão inicial.
 */
import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Loader2,
  Wallet,
  TrendingUp,
  TrendingDown,
  History,
  ChevronDown,
  ChevronRight,
  Receipt,
  AlertTriangle,
  CircleDollarSign,
  Ban,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { InlineNotice } from '@/components/ui/inline-notice';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { formatCurrency } from '@/lib/currency-utils';
import { parseDateLocal, cn } from '@/lib/utils';
import { usePermissions } from '@/hooks/usePermissions';
import { CancelCreditDialog } from './CancelCreditDialog';
import {
  useCustomerCreditHistory,
  type CustomerCreditEvent,
  type CustomerCreditBalance,
} from '@/hooks/financial/useCustomerCreditHistory';
import type { CobrancaProRataItemDiff } from '@/hooks/financial/useCobrancaCalculationMemory';

interface Props {
  customerId: string;
  isActive?: boolean;
}

// ─── Helpers de formatação ────────────────────────────────────────────────────
function fmtDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    return format(new Date(value), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  } catch {
    return '—';
  }
}
function fmtDate(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    const d = value.length === 10 ? parseDateLocal(value) : new Date(value);
    return format(d, 'dd/MM/yyyy', { locale: ptBR });
  } catch {
    return '—';
  }
}
function shortId(id: string | null | undefined): string {
  if (!id) return '—';
  return id.substring(0, 8).toUpperCase();
}
function describeEvent(type: string): string {
  switch (type) {
    case 'mid_cycle_proration_applied':
      return 'Ajuste pro-rata mid-cycle';
    case 'pro_rata_applied_on_create':
      return 'Pro-rata na criação';
    case 'itens_atualizados':
      return 'Itens da assinatura atualizados';
    case 'subscription_cycle_charged':
      return 'Ciclo cobrado';
    case 'subscription_anticipated':
      return 'Antecipação aplicada';
    case 'subscription_created':
      return 'Assinatura criada';
    case 'subscription_updated':
      return 'Assinatura atualizada';
    case 'subscription_canceled':
      return 'Assinatura cancelada';
    case 'subscription_paused':
      return 'Assinatura pausada';
    case 'subscription_resumed':
      return 'Assinatura reativada';
    case 'credit_canceled_manual':
      return 'Crédito cancelado manualmente';
    default:
      return type;
  }
}
function describeOrigem(origem: string): string {
  switch (origem) {
    case 'downgrade':
      return 'Sobra de downgrade';
    case 'plan_change':
      return 'Mudança de plano';
    case 'upgrade_credit':
      return 'Crédito de upgrade';
    case 'refund':
      return 'Reembolso';
    case 'manual':
      return 'Lançamento manual';
    default:
      return origem;
  }
}

// ─── KPI card ─────────────────────────────────────────────────────────────────
function KpiCard({
  label,
  value,
  icon: Icon,
  tone = 'default',
}: {
  label: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
  tone?: 'default' | 'credit' | 'debit' | 'primary';
}) {
  const toneClasses: Record<string, string> = {
    default: 'bg-muted/40 text-foreground',
    credit: 'bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100',
    debit: 'bg-rose-50 text-rose-900 dark:bg-rose-950/40 dark:text-rose-100',
    primary: 'bg-primary/10 text-foreground',
  };
  return (
    <div className={cn('rounded-md px-3 py-2 flex items-center gap-3', toneClasses[tone])}>
      <Icon className="h-4 w-4 opacity-70 shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider opacity-70 truncate">{label}</div>
        <div className="text-sm font-semibold tabular-nums">{value}</div>
      </div>
    </div>
  );
}

// ─── Linha expandida de items_diff ───────────────────────────────────────────
function ItemsDiffTable({ items }: { items: CobrancaProRataItemDiff[] }) {
  if (!items?.length) return null;
  return (
    <div className="mt-2 overflow-x-auto rounded border border-border/40">
      <table className="w-full text-xs">
        <thead className="bg-muted/40">
          <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="text-left py-1.5 px-2">Item</th>
            <th className="text-right py-1.5 px-2">Antes</th>
            <th className="text-right py-1.5 px-2">Depois</th>
            <th className="text-right py-1.5 px-2">Crédito</th>
            <th className="text-right py-1.5 px-2">Débito</th>
            <th className="text-right py-1.5 px-2">Delta</th>
          </tr>
        </thead>
        <tbody>
          {items.map((d, idx) => {
            const nome = d.novo?.descricao ?? d.antigo?.descricao ?? '—';
            const semMudanca = d.skipped;
            return (
              <tr key={idx} className="border-t border-border/30">
                <td className="py-1.5 px-2 font-medium">
                  {nome}
                  {semMudanca && (
                    <span className="text-muted-foreground ml-1 font-normal">(sem mudança)</span>
                  )}
                </td>
                <td className="text-right py-1.5 px-2 text-muted-foreground">
                  {d.antigo
                    ? `${d.antigo.quantidade ?? 1}× ${formatCurrency(d.antigo.valor_unitario ?? 0)}`
                    : '—'}
                </td>
                <td className="text-right py-1.5 px-2">
                  {d.novo
                    ? `${d.novo.quantidade ?? 1}× ${formatCurrency(d.novo.valor_unitario ?? 0)}`
                    : '—'}
                </td>
                <td className="text-right py-1.5 px-2 text-emerald-700 dark:text-emerald-400">
                  {d.credito > 0 ? formatCurrency(d.credito) : '—'}
                </td>
                <td className="text-right py-1.5 px-2">
                  {d.debito > 0 ? formatCurrency(d.debito) : '—'}
                </td>
                <td
                  className={cn(
                    'text-right py-1.5 px-2 font-semibold tabular-nums',
                    d.delta > 0 && 'text-foreground',
                    d.delta < 0 && 'text-emerald-700 dark:text-emerald-400',
                  )}
                >
                  {d.delta === 0 ? formatCurrency(0) : formatCurrency(d.delta)}
                </td>
              </tr>
            );
          })}
        </tbody>
        {/* Totalizador: soma de Crédito, Débito e Delta no rodapé da tabela */}
        <tfoot className="bg-muted/30 border-t-2 border-border/60">
          {(() => {
            const totalCredito = items.reduce((acc, d) => acc + (d.credito || 0), 0);
            const totalDebito = items.reduce((acc, d) => acc + (d.debito || 0), 0);
            const totalDelta = items.reduce((acc, d) => acc + (d.delta || 0), 0);
            return (
              <tr className="font-semibold">
                <td colSpan={3} className="py-1.5 px-2 text-right uppercase text-[10px] tracking-wider text-muted-foreground">
                  Total
                </td>
                <td className="text-right py-1.5 px-2 text-emerald-700 dark:text-emerald-400 tabular-nums">
                  {formatCurrency(totalCredito)}
                </td>
                <td className="text-right py-1.5 px-2 tabular-nums">
                  {formatCurrency(totalDebito)}
                </td>
                <td
                  className={cn(
                    'text-right py-1.5 px-2 tabular-nums font-bold',
                    totalDelta < 0 && 'text-emerald-700 dark:text-emerald-400',
                  )}
                >
                  {formatCurrency(totalDelta)}
                </td>
              </tr>
            );
          })()}
        </tfoot>

      </table>
    </div>
  );
}

// ─── Card de evento ──────────────────────────────────────────────────────────
function EventCard({ ev }: { ev: CustomerCreditEvent }) {
  const [open, setOpen] = useState(false);
  const hasDiff = (ev.items_diff?.length ?? 0) > 0;
  const movimenta = ev.credito_total > 0 || ev.debito_total > 0 || (ev.delta_total ?? 0) !== 0;

  // v1.3.0 — Detalhes do cancelamento manual de crédito (metadata do evento).
  const isCancelamento = ev.event_type === 'credit_canceled_manual';
  const md = (ev.metadata ?? {}) as Record<string, any>;
  const cancelInfo = isCancelamento
    ? {
        saldoCancelado: Number(md.saldo_cancelado ?? 0),
        valor: md.valor != null ? Number(md.valor) : null,
        valorUsado: md.valor_usado != null ? Number(md.valor_usado) : null,
        origem: typeof md.origem === 'string' ? md.origem : null,
        // Texto puro (sem HTML) — o backend já sanitiza, aqui apenas exibimos como texto.
        motivo: typeof md.cancel_motivo === 'string' ? md.cancel_motivo : null,
      }
    : null;

  return (
    <div className="border border-border/60 rounded-md p-3 bg-card">
      <div className="flex items-start gap-3">
        <div className="mt-0.5">
          {movimenta ? (
            <CircleDollarSign className="h-4 w-4 text-primary" />
          ) : (
            <History className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold">{describeEvent(ev.event_type)}</span>
            {ev.ciclo_numero != null && (
              <Badge variant="secondary" className="text-[10px]">
                Ciclo #{ev.ciclo_numero}
              </Badge>
            )}
            {ev.ciclo_origem != null && (
              <Badge variant="secondary" className="text-[10px]">
                Originada do Ciclo #{ev.ciclo_origem}
              </Badge>
            )}
            {ev.cobranca && !ev.cobranca.existe && (
              <Badge variant="outline" className="text-[10px] gap-1">
                <AlertTriangle className="h-3 w-3" />
                Cobrança excluída — histórico preservado
              </Badge>
            )}
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5">
            <span>{fmtDateTime(ev.created_at)}</span>
            {ev.actor_full_name && <span>por {ev.actor_full_name}</span>}
            {ev.assinatura && <span>Assinatura {shortId(ev.assinatura.id)}</span>}
            {ev.cobranca && (
              <span className="inline-flex items-center gap-1">
                <Receipt className="h-3 w-3" />
                Cobrança {shortId(ev.cobranca.id)}
                {ev.cobranca.valor_total != null && ` · ${formatCurrency(ev.cobranca.valor_total)}`}
              </span>
            )}
          </div>

          {/* v1.3.0 — Detalhamento do cancelamento manual de crédito */}
          {cancelInfo && (
            <div className="mt-2 rounded-md border border-amber-300/50 bg-amber-50/60 dark:border-amber-900/50 dark:bg-amber-950/20 p-2 space-y-1">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                <span className="inline-flex items-center gap-1 text-amber-800 dark:text-amber-300">
                  <Ban className="h-3 w-3" />
                  Crédito cancelado{' '}
                  <span className="font-semibold tabular-nums">
                    {formatCurrency(cancelInfo.saldoCancelado)}
                  </span>
                </span>
                {cancelInfo.origem && (
                  <span className="text-muted-foreground">
                    Origem: {describeOrigem(cancelInfo.origem)}
                  </span>
                )}
                {ev.credit_id && (
                  <span className="text-muted-foreground font-mono">
                    Crédito {shortId(ev.credit_id)}
                  </span>
                )}
              </div>
              {(cancelInfo.valor != null || cancelInfo.valorUsado != null) && (
                <div className="text-[11px] text-muted-foreground">
                  Valor original{' '}
                  <span className="tabular-nums">{formatCurrency(cancelInfo.valor ?? 0)}</span>
                  {' · '}Já utilizado{' '}
                  <span className="tabular-nums">{formatCurrency(cancelInfo.valorUsado ?? 0)}</span>
                </div>
              )}
              {cancelInfo.motivo && (
                <div className="text-[11px] text-foreground/90">
                  <span className="text-muted-foreground">Motivo: </span>
                  {cancelInfo.motivo}
                </div>
              )}
            </div>
          )}


          {movimenta && (
            <div className="mt-2 flex flex-wrap gap-3 text-xs">
              {ev.credito_total > 0 && (
                <span className="text-emerald-700 dark:text-emerald-400">
                  Crédito gerado{' '}
                  <span className="font-semibold tabular-nums">
                    {formatCurrency(ev.credito_total)}
                  </span>
                </span>
              )}
              {ev.debito_total > 0 && (
                <span>
                  Débito gerado{' '}
                  <span className="font-semibold tabular-nums">
                    {formatCurrency(ev.debito_total)}
                  </span>
                </span>
              )}
              {ev.delta_total != null && (
                <span>
                  Delta líquido{' '}
                  <span
                    className={cn(
                      'font-semibold tabular-nums',
                      ev.delta_total < 0 && 'text-emerald-700 dark:text-emerald-400',
                    )}
                  >
                    {formatCurrency(ev.delta_total)}
                  </span>
                </span>
              )}
              {ev.factor != null && (
                <span className="text-muted-foreground">
                  Fator <span className="font-mono">{ev.factor.toFixed(6).replace('.', ',')}</span>
                </span>
              )}
              {/* v1.1.0 — Crédito do cliente aplicado para abater a dívida */}
              {ev.creditos_consumidos != null && ev.creditos_consumidos > 0 && (
                <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400">
                  <Wallet className="h-3 w-3" />
                  Crédito aplicado{' '}
                  <span className="font-semibold tabular-nums">
                    − {formatCurrency(ev.creditos_consumidos)}
                  </span>
                </span>
              )}
              {/* v1.1.0 — Valor que efetivamente foi para a cobrança após o abatimento */}
              {ev.creditos_consumidos != null && ev.creditos_consumidos > 0
                && ev.cobranca?.valor_total != null && (
                <span>
                  Cobrança gerada{' '}
                  <span className="font-semibold tabular-nums">
                    {formatCurrency(ev.cobranca.valor_total)}
                  </span>
                </span>
              )}
            </div>
          )}

          {/* v1.1.0 — Linha explicativa do abatimento do crédito */}
          {ev.creditos_consumidos != null
            && ev.creditos_consumidos > 0
            && ev.delta_total != null
            && ev.cobranca?.valor_total != null && (
            <div className="mt-1 text-[11px] text-muted-foreground">
              Débito bruto{' '}
              <span className="tabular-nums">{formatCurrency(ev.delta_total)}</span>{' '}
              <span className="mx-0.5">−</span>{' '}
              Crédito aplicado{' '}
              <span className="tabular-nums text-amber-700 dark:text-amber-400">
                {formatCurrency(ev.creditos_consumidos)}
              </span>{' '}
              <span className="mx-0.5">=</span>{' '}
              Cobrança gerada{' '}
              <span className="tabular-nums font-medium text-foreground">
                {formatCurrency(ev.cobranca.valor_total)}
              </span>
            </div>
          )}

          {hasDiff && (
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className="mt-2 inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
            >
              {open ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
              {open ? 'Ocultar composição' : 'Ver composição antes × depois'}
            </button>
          )}

          {open && hasDiff && <ItemsDiffTable items={ev.items_diff} />}
        </div>
      </div>
    </div>
  );
}

// ─── Componente principal ────────────────────────────────────────────────────
export function CustomerCreditHistoryTab({ customerId, isActive = true }: Props) {
  const [showSaldos, setShowSaldos] = useState(true);
  // v1.3.0: seção recolhível de créditos cancelados manualmente
  const [showCancelados, setShowCancelados] = useState(false);
  // v1.2.0: estado do modal de cancelamento manual de crédito
  const [creditoParaCancelar, setCreditoParaCancelar] = useState<CustomerCreditBalance | null>(null);

  // v1.2.0: cancelamento é gated pela permissão granular financial_charges.edit
  const { canEdit } = usePermissions();
  const podeCancelarCredito = canEdit('financial_charges');

  const { data, isLoading, error } = useCustomerCreditHistory({
    customerId,
    enabled: isActive,
    limit: 200,
  });

  const resumo = data?.resumo;
  const eventos = data?.eventos ?? [];
  const saldos = data?.saldos ?? [];

  // v1.1.0: Filtro alinhado ao backend (`get_customer_credit_history` /
  // `saldo_credito_disponivel`), que considera crédito disponível quando
  // status = 'active' e (expira_em IS NULL OR expira_em >= hoje). O rótulo
  // legado 'disponivel' é aceito como fallback defensivo.
  const saldosAtivos = useMemo(
    () =>
      saldos.filter((s) => {
        const statusOk = s.status === 'active' || s.status === 'disponivel';
        const saldoOk = Number(s.saldo) > 0;
        const naoExpirado =
          !s.expira_em ||
          new Date(s.expira_em) >= new Date(new Date().toDateString());
        return statusOk && saldoOk && naoExpirado;
      }),
    [saldos],
  );

  // v1.3.0: créditos cancelados manualmente (auditoria — não contam como saldo).
  const saldosCancelados = useMemo(
    () => saldos.filter((s) => s.status === 'canceled'),
    [saldos],
  );

  return (
    <div className="h-full overflow-auto px-4 pb-4 pt-3 space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <KpiCard
          label="Saldo disponível"
          value={formatCurrency(resumo?.saldo_credito_disponivel ?? 0)}
          icon={Wallet}
          tone="credit"
        />
        <KpiCard
          label="Créditos gerados (vida)"
          value={formatCurrency(resumo?.total_creditos_gerados ?? 0)}
          icon={TrendingUp}
          tone="credit"
        />
        <KpiCard
          label="Créditos aplicados (vida)"
          value={formatCurrency(resumo?.total_creditos_aplicados ?? 0)}
          icon={CircleDollarSign}
          tone="primary"
        />
        <KpiCard
          label="Débitos gerados (vida)"
          value={formatCurrency(resumo?.total_debitos_gerados ?? 0)}
          icon={TrendingDown}
          tone="debit"
        />
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Carregando histórico…</span>
        </div>
      )}

      {error && (
        <InlineNotice variant="error">
          Não foi possível carregar o histórico de créditos deste cliente.
        </InlineNotice>
      )}

      {data && !isLoading && (
        <>
          {/* Saldos ativos */}
          <section>
            <button
              type="button"
              onClick={() => setShowSaldos((s) => !s)}
              className="w-full flex items-center justify-between text-sm font-semibold py-1"
            >
              <span className="flex items-center gap-2">
                <Wallet className="h-4 w-4 text-primary" />
                Saldos ativos
                <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-mono">
                  {saldosAtivos.length}
                </Badge>
              </span>
              {showSaldos ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>

            {showSaldos && (
              <div className="mt-2">
                {saldosAtivos.length === 0 ? (
                  <div className="text-xs text-muted-foreground py-3">
                    Nenhum crédito disponível no momento.
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded border border-border/40">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/40">
                        <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                          <th className="text-left py-1.5 px-2">ID</th>
                          <th className="text-left py-1.5 px-2">Origem</th>
                          <th className="text-right py-1.5 px-2">Valor</th>
                          <th className="text-right py-1.5 px-2">Usado</th>
                          <th className="text-right py-1.5 px-2">Saldo</th>
                          <th className="text-left py-1.5 px-2">Expira</th>
                          <th className="text-left py-1.5 px-2">Assinatura</th>
                          <th className="text-left py-1.5 px-2">Criado</th>
                          {/* v1.2.0: coluna de ações (cancelar crédito) */}
                          {podeCancelarCredito && (
                            <th className="text-right py-1.5 px-2">Ações</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {saldosAtivos.map((s) => (
                          <tr key={s.id} className="border-t border-border/30">
                            <td className="py-1.5 px-2 font-mono">{shortId(s.id)}</td>
                            <td className="py-1.5 px-2">{describeOrigem(s.origem)}</td>
                            <td className="text-right py-1.5 px-2 tabular-nums">
                              {formatCurrency(s.valor)}
                            </td>
                            <td className="text-right py-1.5 px-2 tabular-nums text-muted-foreground">
                              {formatCurrency(s.valor_usado)}
                            </td>
                            <td className="text-right py-1.5 px-2 font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">
                              {formatCurrency(s.saldo)}
                            </td>
                            <td className="py-1.5 px-2">
                              {s.expira_em ? fmtDate(s.expira_em) : '—'}
                            </td>
                            <td className="py-1.5 px-2 font-mono">{shortId(s.assinatura_id)}</td>
                            <td className="py-1.5 px-2 text-muted-foreground">
                              {fmtDate(s.created_at)}
                            </td>
                            {podeCancelarCredito && (
                              <td className="py-1 px-2 text-right">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 text-destructive hover:text-destructive"
                                      onClick={() => setCreditoParaCancelar(s)}
                                      aria-label="Cancelar crédito"
                                    >
                                      <Ban className="h-3.5 w-3.5" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Cancelar crédito</TooltipContent>
                                </Tooltip>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* v1.3.0 — Créditos cancelados manualmente (auditoria) */}
          {saldosCancelados.length > 0 && (
            <section>
              <button
                type="button"
                onClick={() => setShowCancelados((s) => !s)}
                className="w-full flex items-center justify-between text-sm font-semibold py-1"
              >
                <span className="flex items-center gap-2">
                  <Ban className="h-4 w-4 text-destructive" />
                  Créditos cancelados
                  <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-mono">
                    {saldosCancelados.length}
                  </Badge>
                </span>
                {showCancelados ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>

              {showCancelados && (
                <div className="mt-2 overflow-x-auto rounded border border-border/40">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40">
                      <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        <th className="text-left py-1.5 px-2">ID</th>
                        <th className="text-left py-1.5 px-2">Origem</th>
                        <th className="text-right py-1.5 px-2">Valor</th>
                        <th className="text-right py-1.5 px-2">Cancelado</th>
                        <th className="text-left py-1.5 px-2">Data</th>
                        <th className="text-left py-1.5 px-2">Por</th>
                        <th className="text-left py-1.5 px-2">Motivo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {saldosCancelados.map((s) => {
                        const meta = (s.metadata ?? {}) as Record<string, any>;
                        const motivo =
                          typeof meta.cancel_motivo === 'string' ? meta.cancel_motivo : '—';
                        const saldoCancelado = Number(meta.saldo_cancelado ?? s.saldo ?? 0);
                        return (
                          <tr key={s.id} className="border-t border-border/30">
                            <td className="py-1.5 px-2 font-mono">{shortId(s.id)}</td>
                            <td className="py-1.5 px-2">{describeOrigem(s.origem)}</td>
                            <td className="text-right py-1.5 px-2 tabular-nums">
                              {formatCurrency(s.valor)}
                            </td>
                            <td className="text-right py-1.5 px-2 tabular-nums font-semibold text-amber-700 dark:text-amber-400">
                              {formatCurrency(saldoCancelado)}
                            </td>
                            <td className="py-1.5 px-2 text-muted-foreground">
                              {meta.canceled_at ? fmtDateTime(meta.canceled_at) : '—'}
                            </td>
                            <td className="py-1.5 px-2 text-muted-foreground">
                              {meta.canceled_by_name || '—'}
                            </td>
                            <td className="py-1.5 px-2 max-w-[280px]">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="block truncate">{motivo}</span>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">{motivo}</TooltipContent>
                              </Tooltip>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}




          <Separator />

          {/* Timeline */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <History className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold">Linha do tempo de movimentações</h3>
              <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-mono">
                {eventos.length}
              </Badge>
            </div>

            {eventos.length === 0 ? (
              <div className="text-xs text-muted-foreground py-6 text-center border border-dashed rounded-md">
                Nenhum evento registrado para este cliente.
              </div>
            ) : (
              <div className="space-y-2">
                {eventos.map((ev) => (
                  <EventCard key={ev.id} ev={ev} />
                ))}
              </div>
            )}

            <div className="mt-2 text-[10px] text-muted-foreground">
              O histórico é imutável e independente das cobranças. Mesmo que uma cobrança seja
              excluída, o registro do ajuste de crédito permanece preservado para auditoria.
            </div>
          </section>
        </>
      )}

      {/* v1.2.0 — Modal de cancelamento manual de crédito */}
      <CancelCreditDialog
        open={!!creditoParaCancelar}
        onOpenChange={(o) => {
          if (!o) setCreditoParaCancelar(null);
        }}
        credito={
          creditoParaCancelar
            ? {
                id: creditoParaCancelar.id,
                saldo: Number(creditoParaCancelar.saldo ?? 0),
                origem: creditoParaCancelar.origem,
              }
            : null
        }
        customerId={customerId}
      />
    </div>
  );
}
